import { useEffect } from 'react'
import { useController, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { S3Bucket } from '@/types'
import { ProgressionProgramTier } from '@/api/dashboard'
import { ButtonIcon, FieldGroup, Header, ImageUploader } from '@/ui'
import { CloseOutline } from '@/icons'
import { Button, Input, formatHookFormErrors } from '@dashboard/ui'
import { formatNumber } from '@/util'

const MIN_POINTS = 1
const MAX_POINTS = 1_000_000

interface ProgressionTierFormProps {
  tier: ProgressionProgramTier
  onSubmit: (data: ProgressionProgramTier) => void
  onClose: () => void
}

export const ProgressionTierForm = ({ tier, onSubmit, onClose }: ProgressionTierFormProps) => {
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<ProgressionProgramTier>()
  const { field: image } = useController({ name: 'image_url', control })
  const { field: backgroundImage } = useController({ name: 'background_image_url', control })
  useEffect(() => reset(tier), [tier, reset])

  const { t } = useTranslation()

  return (
    <form
      onSubmit={handleSubmit(data => {
        onSubmit(data)
        onClose()
      })}
    >
      <Header
        title={<span className="font-nohemi text-paragraph-md font-medium">{t('progression.level.settings')}</span>}
        className="p-4"
        minWidth="auto"
      >
        <div className="flex gap-4">
          <Button variant="default" type="submit">
            {t('apply')}
          </Button>
          <ButtonIcon onClick={onClose} type="reset" size="sm" variant="outline" color="secondary">
            <CloseOutline />
          </ButtonIcon>
        </div>
      </Header>

      <div className="flex size-full flex-col gap-4 p-4">
        <FieldGroup label={t('progression.rank_name')}>
          <Input {...formatHookFormErrors(errors, 'rank_name')} {...register('rank_name')} />
        </FieldGroup>
        <FieldGroup label={t('progression.value')}>
          <Input
            type="number"
            {...formatHookFormErrors(errors, 'value')}
            {...register('value', {
              required: t('validation.required'),
              min: {
                value: MIN_POINTS,
                message: t('validation.min', { value: formatNumber(MIN_POINTS), field: t('progression.value') }),
              },
              max: {
                value: MAX_POINTS,
                message: t('validation.max', { value: formatNumber(MAX_POINTS), field: t('progression.value') }),
              },
              valueAsNumber: true,
            })}
          />
        </FieldGroup>

        <FieldGroup
          size="sm"
          label={<span className="text-caption-sm font-normal text-text-tertiary">{t('Image')}</span>}
        >
          <ImageUploader
            {...image}
            bucket={S3Bucket.hub}
            accept={{
              'image/jpeg': ['.jpeg', '.jpg'],
              'image/png': [],
              'image/webp': [],
            }}
            recommendedSize={[512, 512]}
          />
        </FieldGroup>

        <FieldGroup
          size="sm"
          label={<span className="text-caption-sm font-normal text-text-tertiary">{t('background-image')}</span>}
        >
          <ImageUploader
            {...backgroundImage}
            bucket={S3Bucket.hub}
            accept={{
              'image/jpeg': ['.jpeg', '.jpg'],
              'image/png': [],
              'image/webp': [],
            }}
            recommendedSize={[512, 512]}
          />
        </FieldGroup>
      </div>
    </form>
  )
}
